import { Controller } from "@hotwired/stimulus"

// A daily report's History popup (Phelipe 2026-09-22): the button by the
// date opens it, the list comes from history.json, newest first — who did
// what, from the phone or the web, and before/after for edited fields.
// Never part of the PDF.
export default class extends Controller {
  static targets = ["modal", "panel", "list", "filters", "subtitle"]
  // i18n: the pop-up's words in the reader's language, from the page (2026-10-15).
  static values = { url: String, title: String, i18n: Object }

  connect() {
    this.entries = null
    this.current = "all"
    this.onKey = (e) => { if (e.key === "Escape") this.close() }
    // Grab the popup's pieces now, while Stimulus can still find them: the
    // popup moves out of this element's scope the first time it opens.
    this.modal = this.modalTarget
    this.panel = this.panelTarget
    this.list = this.listTarget
    this.filters = this.hasFiltersTarget ? this.filtersTarget : null
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
    // The popup lives on <body> while open (see open); take it along.
    this.modal?.remove()
  }

  open(event) {
    event?.preventDefault()
    this.lift()
    this.modal.classList.remove("hidden")
    document.addEventListener("keydown", this.onKey)
    this.paintFilters()
    if (this.entries) return this.render()
    this.load()
  }

  // The report opens in a side panel that slides in with a CSS transform, and
  // a transformed ancestor traps position:fixed - so the "full-screen" popup
  // used to sit inside the panel, off-centre, with the report's photos
  // drawing over it (Phelipe 2026-09-22). Lifted onto <body> on first open:
  // fixed then means the viewport, centred, above everything. Outside this
  // element Stimulus no longer routes the popup's data-actions, so its
  // clicks are wired here by hand.
  lift() {
    if (this.lifted) return
    this.lifted = true
    document.body.appendChild(this.modal)
    this.modal.addEventListener("click", (e) => this.backdrop(e))
    this.modal.querySelectorAll("[data-action*='report-history#close']").forEach((b) => b.addEventListener("click", () => this.close()))
    this.modal.querySelectorAll("[data-action*='report-history#filter']").forEach((b) => b.addEventListener("click", (e) => this.filter(e)))
  }

  close() {
    this.modal.classList.add("hidden")
    document.removeEventListener("keydown", this.onKey)
  }

  backdrop(event) {
    if (!this.panel.contains(event.target)) this.close()
  }

  async load() {
    try {
      const res = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this.entries = data.history || []
      this.render()
    } catch (e) {
      this.list.innerHTML = `<div class="py-8 text-center text-gray-500">${this.t("history_failed", "The history could not be loaded. Try again.")}</div>`
    }
  }

  filter(event) {
    this.current = event.currentTarget.dataset.filter || "all"
    this.paintFilters()
    this.render()
  }

  paintFilters() {
    if (!this.filters) return
    this.filters.querySelectorAll("button").forEach((b) => {
      const on = (b.dataset.filter || "all") === this.current
      b.style.background = on ? "#c63e1a" : "transparent"
      b.style.borderColor = on ? "#c63e1a" : "rgba(120,113,108,0.6)"
      b.style.color = on ? "#ffffff" : ""
    })
  }

  matches(entry) {
    switch (this.current) {
      case "edits": return entry.action === "updated"
      case "photos": return entry.action.startsWith("photo_")
      case "publishing": return ["published", "unpublished", "sent", "created", "deleted"].includes(entry.action)
      default: return true
    }
  }

  render() {
    const rows = (this.entries || []).filter((e) => this.matches(e))
    if (!rows.length) {
      this.list.innerHTML = `<div class="py-8 text-center text-gray-500">${this.t("history_empty", "Nothing here yet.")}</div>`
      return
    }
    let day = ""
    const html = []
    rows.forEach((entry) => {
      const at = entry.at ? new Date(entry.at) : null
      const label = at ? this.dayLabel(at) : ""
      if (label !== day) {
        day = label
        html.push(`<div class="pt-3 pb-1 text-[11px] font-bold tracking-wider uppercase text-gray-500">${this.esc(label)}</div>`)
      }
      html.push(this.row(entry, at))
    })
    this.list.innerHTML = html.join("")
  }

  row(entry, at) {
    const { bg, fg, icon } = this.look(entry.action)
    const who = entry.user_name ? `<span class="font-semibold">${this.esc(entry.user_name)}</span> ` : ""
    const when = [at ? at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "", this.sourceLabel(entry.source)].filter(Boolean).join(" · ")
    let extra = ""
    if (entry.action === "updated") {
      const parts = Object.entries(entry.changes || {}).map(([field, pair]) => this.change(field, pair)).filter(Boolean)
      extra = parts.join("")
    } else if (entry.action === "sent" && entry.meta?.emails?.length) {
      extra = `<div class="mt-1 text-xs text-gray-500">${this.esc(entry.meta.emails.join(", "))}</div>`
    } else if (entry.action.startsWith("photo_") && entry.meta?.comment) {
      extra = `<div class="mt-1 text-xs text-gray-500">${this.esc(entry.meta.comment)}</div>`
    }
    return `
      <div class="flex gap-3 py-2.5 border-b border-gray-100 dark:border-[#2f2b29]">
        <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style="background:${bg};color:${fg}">${icon}</div>
        <div class="flex-1 min-w-0">
          <div>${who}${this.esc(this.sentence(entry))}</div>
          <div class="text-xs text-gray-500">${this.esc(when)}</div>
          ${extra}
        </div>
      </div>`
  }

  change(field, pair) {
    const [before, after] = Array.isArray(pair) ? pair : [null, pair]
    const name = this.fieldName(field)
    if (field === "notes" || field === "notes2") {
      return `
        <div class="grid grid-cols-2 gap-2 mt-2">
          <div class="rounded-lg px-2.5 py-2 text-xs leading-relaxed" style="background:rgba(185,28,28,0.10);border:1px solid rgba(185,28,28,0.35)"><div class="text-[10px] font-bold uppercase tracking-wider mb-0.5">${this.t("history_before", "Before")}</div>${this.esc(before || "—")}</div>
          <div class="rounded-lg px-2.5 py-2 text-xs leading-relaxed" style="background:rgba(21,128,61,0.10);border:1px solid rgba(21,128,61,0.35)"><div class="text-[10px] font-bold uppercase tracking-wider mb-0.5">${this.t("history_after", "After")}</div>${this.esc(after || "—")}</div>
        </div>`
    }
    return `<div class="mt-1 text-xs text-gray-600 dark:text-gray-300">${this.esc(name)}: <span class="line-through opacity-70">${this.esc(this.plain(before))}</span> → <span class="font-semibold">${this.esc(this.plain(after))}</span></div>`
  }

  sentence(entry) {
    const t = (k, d) => this.t(k, d)
    switch (entry.action) {
      case "created": return t("history_created", "started the report")
      case "updated": {
        const fields = Object.keys(entry.changes || {}).map((f) => this.fieldName(f).toLowerCase())
        return `${t("history_edited", "edited")} ${fields.join(", ") || t("history_the_report", "the report")}`
      }
      case "published": return entry.meta?.first ? t("history_published_new", "published the report") : t("history_published", "published the report")
      case "unpublished": return t("history_unpublished", "reopened the report for editing")
      case "photo_added": return entry.meta?.is_image === false ? t("history_file_added", "attached a file") : t("history_photo_added", "added a photo")
      case "photo_removed": return entry.meta?.is_image === false ? t("history_file_removed", "removed a file") : t("history_photo_removed", "removed a photo")
      case "photo_edited": return t("history_photo_edited", "changed a photo's details")
      case "sent": return `${t("history_sent", "sent the PDF to")} ${entry.meta?.count || 0} ${t("history_people", "people")}`
      case "deleted": return t("history_deleted", "deleted the report")
      default: return entry.action
    }
  }

  fieldName(field) {
    const names = {
      notes: this.t("history_field_notes", "Notes"),
      notes2: this.t("history_field_notes2", "Second notes"),
      workers: this.t("history_field_workers", "Workers on site"),
      weather_delay: this.t("history_field_weather_delay", "Weather delay"),
      date: this.t("history_field_date", "Date"),
      type: this.t("history_field_type", "Report type"),
      number: this.t("history_field_number", "Report number")
    }
    return names[field] || field
  }

  look(action) {
    const icons = {
      check: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
      pencil: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/></svg>',
      camera: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>',
      trash: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>',
      unlock: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>',
      mail: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v16H4z"/><path d="M4 7l8 6 8-6"/></svg>',
      plus: '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>'
    }
    switch (action) {
      case "published": return { bg: "rgba(21,128,61,0.18)", fg: "#15803d", icon: icons.check }
      case "unpublished": return { bg: "rgba(55,48,163,0.18)", fg: "#4f46e5", icon: icons.unlock }
      case "updated": case "photo_edited": return { bg: "rgba(194,65,12,0.18)", fg: "#c2410c", icon: icons.pencil }
      case "photo_added": return { bg: "rgba(120,113,108,0.18)", fg: "#57534e", icon: icons.camera }
      case "photo_removed": case "deleted": return { bg: "rgba(185,28,28,0.18)", fg: "#b91c1c", icon: icons.trash }
      case "sent": return { bg: "rgba(120,113,108,0.18)", fg: "#57534e", icon: icons.mail }
      default: return { bg: "rgba(120,113,108,0.18)", fg: "#57534e", icon: icons.plus }
    }
  }

  sourceLabel(source) {
    if (source === "phone") return this.t("history_phone", "Phone")
    if (source === "web") return this.t("history_web", "Web")
    return ""
  }

  dayLabel(at) {
    const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1)
    const same = (a, b) => a.toDateString() === b.toDateString()
    const date = at.toLocaleDateString([], { month: "short", day: "numeric" })
    if (same(at, today)) return `${this.t("today", "Today")} · ${date}`
    if (same(at, y)) return `${this.t("yesterday", "Yesterday")} · ${date}`
    return at.toLocaleDateString([], { month: "short", day: "numeric", year: at.getFullYear() === today.getFullYear() ? undefined : "numeric" })
  }

  plain(value) {
    if (value === null || value === undefined || value === "") return "—"
    return String(value)
  }

  t(key, fallback) {
    const page = this.hasI18nValue ? this.i18nValue : {}
    const dict = window.I18nHistory || {}
    return page[key] || dict[key] || fallback
  }

  esc(text) {
    return String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
